import { CalendarDays } from 'lucide-react';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';

import { cn } from '../lib/utils';
import { editDate, toDisplay, toIso } from './date-text';
import { Input } from './ui/input';

interface DateInputProps {
  id?: string;
  name?: string;
  /** `YYYY-MM-DD`, or '' for no date: the form's value, whatever the field shows. */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** `YYYY-MM-DD` bounds, offered by the calendar. */
  min?: string;
  max?: string;
  disabled?: boolean;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/**
 * A date field that reads and is typed as MM/DD/YYYY whatever the browser's language, which the
 * browser's own date input cannot promise: Chrome draws that in the operating system's order.
 * The value in and out stays `YYYY-MM-DD`. The calendar button opens the browser's own picker,
 * so the calendar is still the native, accessible one.
 */
export function DateInput({
  id,
  name,
  value,
  onChange,
  onBlur,
  min,
  max,
  disabled,
  className,
  'aria-invalid': invalid,
  'aria-describedby': describedBy,
}: DateInputProps) {
  const field = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(() => toDisplay(value));

  // A reset or a picked day changes the value from outside; show it, unless it is what is typed.
  useEffect(() => {
    setText((current) => (toIso(current) === value ? current : toDisplay(value)));
  }, [value]);

  const type = (event: ChangeEvent<HTMLInputElement>): void => {
    const raw = event.target.value;
    const { text: next, caret } = editDate(
      raw,
      event.target.selectionStart ?? raw.length,
      raw.length > text.length,
    );
    setText(next);
    // React writes the new text after this handler, which sends the caret to the end; put it
    // back where the person is typing once that has happened.
    requestAnimationFrame(() => field.current?.setSelectionRange(caret, caret));

    if (next === '') onChange('');
    // A half-typed day changes nothing yet: the form keeps the last whole one.
    const iso = toIso(next);
    if (iso) onChange(iso);
  };

  const leave = (): void => {
    // Left half-typed or impossible, the field goes back to the day the form holds; a whole day
    // typed as 7/1/2026 is tidied to 07/01/2026.
    if (text !== '') setText(toDisplay(toIso(text) ?? value));
    onBlur?.();
  };

  const openCalendar = (): void => {
    const element = picker.current;
    if (!element) return;

    try {
      element.showPicker();
    } catch {
      element.focus();
    }
  };

  return (
    <div className={cn('relative cursor-text', className)}>
      <Input
        ref={field}
        id={id}
        name={name}
        inputMode="numeric"
        autoComplete="off"
        placeholder="MM/DD/YYYY"
        value={text}
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={type}
        onBlur={leave}
        className="pr-10"
      />
      <button
        type="button"
        aria-label="Open calendar"
        disabled={disabled}
        onClick={openCalendar}
        className="absolute top-1/2 right-1 flex size-7 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
      >
        <CalendarDays className="size-4" aria-hidden />
      </button>
      {/* The browser's picker, opened by the button; it is never shown or tabbed to itself. */}
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="pointer-events-none absolute right-0 bottom-0 size-px overflow-hidden opacity-0 [clip-path:inset(50%)]"
      />
    </div>
  );
}
